/**
 * Product Hub — warehouses and which ones may serve a given store.
 *
 * The resolver here is the one the spec singles out for a test by name, because the natural
 * "optimisation" of it is wrong in a way that produces no error — only a quietly incorrect answer
 * about where stock comes from. See `resolveEligibleWarehouses`.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/warehouse`.
 */
import { PhOption, phOptions } from './productHub.core';

/* ------------------------------------------------------------------ */
/* Warehouses                                                          */
/* ------------------------------------------------------------------ */

/**
 * What kind of place this is (spec §20).
 *
 * `supplier` and `pickup_point` are here because they are places stock can be *fulfilled from*
 * without us owning it — which is exactly why `holdsPhysicalInventory` is a separate flag rather
 * than being derived from the type. A supplier warehouse ships our orders and never appears in our
 * stock count; a pickup point holds a shelf of it.
 *
 * **`external` was removed.** It never said anything the other three did not: a place we do not own
 * that ships for us is a `supplier`, one that holds a shelf for collection is a `pickup_point`, and
 * "external" in front of somebody choosing between them only invited the wrong answer. Nothing in
 * the ladder or the stock arithmetic ever branched on it. Removing the value from this list is what
 * takes it out of the form, the filter **and** the Joi schema at once — the three read this array.
 *
 * No warehouse held it, so there is nothing to migrate. If one ever turns up — restored from an old
 * dump, say — `warehouseTypeLabel` falls through to the raw string rather than rendering blank, and
 * the row stays editable as long as its type is changed to one of these three on the way out.
 */
export const WAREHOUSE_TYPES = ['internal', 'supplier', 'pickup_point'] as const;
export type WarehouseType = (typeof WAREHOUSE_TYPES)[number];

export const WAREHOUSE_TYPE_LABELS: Record<WarehouseType, string> = {
  internal: 'מחסן פנימי',
  supplier: 'מחסן ספק',
  pickup_point: 'נקודת איסוף'
};

export const WAREHOUSE_TYPE_OPTIONS: PhOption<WarehouseType>[] = phOptions(WAREHOUSE_TYPES, WAREHOUSE_TYPE_LABELS);

/* ------------------------------------------------------------------ */
/* Assignments                                                         */
/* ------------------------------------------------------------------ */

/** The three levels a warehouse can be attached at, most specific first. */
export const ASSIGNMENT_TYPES = ['product', 'category', 'store'] as const;
export type AssignmentType = (typeof ASSIGNMENT_TYPES)[number];

export const ASSIGNMENT_TYPE_LABELS: Record<AssignmentType, string> = {
  product: 'לפי מוצר',
  category: 'לפי קטגוריה',
  store: 'לפי חנות'
};

export const ASSIGNMENT_TYPE_OPTIONS: PhOption<AssignmentType>[] = phOptions(ASSIGNMENT_TYPES, ASSIGNMENT_TYPE_LABELS);

/** Where the answer came from. `default_warehouse` is the last rung of the ladder (§28). */
export const ELIGIBILITY_SOURCES = ['product', 'category', 'store', 'default_warehouse', 'none'] as const;
export type EligibilitySource = (typeof ELIGIBILITY_SOURCES)[number];

export const ELIGIBILITY_SOURCE_LABELS: Record<EligibilitySource, string> = {
  product: 'שיוך לפי מוצר',
  category: 'שיוך לפי קטגוריה',
  store: 'שיוך לפי חנות',
  default_warehouse: 'מחסן ברירת מחדל',
  none: 'אין מחסן זכאי'
};

/** One row of `WarehouseAssignments`, as the resolver needs it. */
export interface WarehouseAssignmentRow {
  id: number;
  warehouseId: number;
  assignmentType: AssignmentType;
  productId: number | null;
  categoryId: number | null;
  storeId: number | null;
  priority: number;
  active: boolean;
}

/** What the resolver is asked about. `categoryIds` is the ancestor chain, deepest first. */
export interface EligibilityQuery {
  productId: number;
  /** The product's category and every ancestor above it, deepest first (§28 via ADR-009). */
  categoryIds: number[];
  storeId: number | null;
}

export interface EligibleWarehouses {
  /** Warehouse ids, in the order stock should be drawn from them. */
  warehouseIds: number[];
  source: EligibilitySource;
  sourceLabel: string;
  /** The assignment rows that won, for a UI that has to explain itself. */
  appliedAssignmentIds: number[];
}

/**
 * Which warehouses may serve this store for this product — **ADR-009, and the trap in it**.
 *
 * The ladder is `product → category → store → default warehouse`, and the rule is: take the
 * **first level that matches anything at all**, then order *within that level* by `priority ASC`
 * and `id ASC`.
 *
 * ## What must never be done instead
 *
 * The tempting implementation is to collect every matching assignment and sort the lot by
 * `priority`. It is shorter, it looks equivalent, and it is wrong: a blanket store-level rule with
 * `priority = 1` would then beat a deliberate product-level assignment with `priority = 100`. The
 * person who configured that product would see their setting ignored, with nothing anywhere
 * reporting a problem — stock would simply be drawn from the wrong building.
 *
 * The spec (§28) states it as a prohibition — "priority may not cause a Store rule to bypass a
 * Product assignment" — and requires a test for it. That test is in the spec file next to this one
 * and is the reason this function is not five lines shorter.
 *
 * Within a level, `id ASC` after `priority ASC` is an addition beyond the spec: two rows with the
 * same priority would otherwise come back in whatever order the database felt like, and "which
 * warehouse did we ship from?" would change between identical requests.
 */
export const resolveEligibleWarehouses = (
  assignments: WarehouseAssignmentRow[],
  query: EligibilityQuery,
  defaultWarehouseId: number | null
): EligibleWarehouses => {
  const active = assignments.filter(row => row.active);

  const answer = (source: EligibilitySource, rows: WarehouseAssignmentRow[]): EligibleWarehouses => ({
    // Ordered inside the level only. Duplicates collapse — two assignments may name one warehouse.
    warehouseIds: [...new Set(sortWithinLevel(rows).map(row => row.warehouseId))],
    source,
    sourceLabel: ELIGIBILITY_SOURCE_LABELS[source],
    appliedAssignmentIds: sortWithinLevel(rows).map(row => row.id)
  });

  const productLevel = active.filter(row => row.assignmentType === 'product' && row.productId === query.productId);
  if (productLevel.length) return answer('product', productLevel);

  // The category level walks the ancestor chain deepest first, so a rule on the immediate category
  // wins over one on its parent — the same specificity idea one rung down.
  for (const categoryId of query.categoryIds) {
    const categoryLevel = active.filter(row => row.assignmentType === 'category' && row.categoryId === categoryId);
    if (categoryLevel.length) return answer('category', categoryLevel);
  }

  if (query.storeId !== null) {
    const storeLevel = active.filter(row => row.assignmentType === 'store' && row.storeId === query.storeId);
    if (storeLevel.length) return answer('store', storeLevel);
  }

  if (defaultWarehouseId !== null) {
    return {
      warehouseIds: [defaultWarehouseId],
      source: 'default_warehouse',
      sourceLabel: ELIGIBILITY_SOURCE_LABELS.default_warehouse,
      appliedAssignmentIds: []
    };
  }

  // Not an error to compute — but the caller must treat it as one before promising availability.
  return {
    warehouseIds: [],
    source: 'none',
    sourceLabel: ELIGIBILITY_SOURCE_LABELS.none,
    appliedAssignmentIds: []
  };
};

const sortWithinLevel = (rows: WarehouseAssignmentRow[]): WarehouseAssignmentRow[] =>
  [...rows].sort((a, b) => a.priority - b.priority || a.id - b.id);

/* ------------------------------------------------------------------ */
/* Stock arithmetic that belongs to a level, not to the whole module   */
/* ------------------------------------------------------------------ */

/**
 * `available = max(0, onHand - reserved)` (spec §21).
 *
 * Clamped at zero because `onHand` may legitimately be negative: §24 of the business-logic spec
 * lists negative stock as an anomaly the dashboard **reports**, not a state the database forbids.
 * A negative `available` would propagate into every sellable calculation as a silent debt.
 */
export const availableOf = (level: { onHand: number; reserved: number }): number =>
  Math.max(0, level.onHand - level.reserved);

/**
 * How much of this shelf may actually be **sold** — `available`, capped by the warehouse's ceiling
 * (ADR-018).
 *
 * `sellableQuantity === null` means nobody set a ceiling, so the answer is the physical figure.
 * A ceiling of **0** is a real answer — "do not sell from this warehouse" — which is why the check
 * is `=== null` and not falsy: `||` here would read 0 as "unset" and quietly sell from a shelf
 * somebody closed.
 *
 * ## This is the only change to the availability calculation, and that is deliberate
 *
 * `resolveStoreSellable` and the three mode formulas (`shared` / `allocated` / `hybrid`, ADR-008)
 * are untouched. What changes is the **input** they receive: the services swap `availableOf` for
 * `sellableOf` when they sum up `eligibleAvailable`. So the spec's worked example (100/20/10/0 →
 * 90/80/70) keeps passing unchanged, and a ceiling narrows the pool the modes then divide.
 *
 * `available` remains the **physical** number and is displayed as such on the stock screens;
 * `sellable` is what may be sold. Two different questions about the same row — the same distinction
 * the module already draws between `available: 9` and `sellable: 3`.
 *
 * `sellableQuantity` is **required** in the argument type, not optional. A caller that forgot to put
 * the column in its `attributes` list would otherwise pass `undefined`, the ceiling would be ignored
 * in silence, and no typecheck anywhere would say a word.
 */
export const sellableOf = (level: { onHand: number; reserved: number; sellableQuantity: number | null }): number => {
  const available = availableOf(level);
  // Clamped at zero for the same reason `availableOf` is: a negative ceiling is not a debt.
  return level.sellableQuantity === null ? available : Math.min(available, Math.max(0, level.sellableQuantity));
};

/**
 * The low-stock threshold in force for one warehouse (spec §22).
 *
 * `override ?? variant default`, and `?? ` rather than `||` on purpose: an override of **0** means
 * "never warn me about this one here", and `||` would discard it in favour of the variant default.
 */
export const effectiveLowStockThreshold = (
  override: number | null | undefined,
  variantDefault: number | null | undefined
): number | null => override ?? variantDefault ?? null;

/** Is this level at or below its threshold? `null` threshold means nobody asked to be warned. */
export const isLowStock = (
  level: { onHand: number; reserved: number },
  override: number | null | undefined,
  variantDefault: number | null | undefined
): boolean => {
  const threshold = effectiveLowStockThreshold(override, variantDefault);
  return threshold !== null && availableOf(level) <= threshold;
};

/**
 * The three states the stock column shows (spec §26, "Inventory Status").
 *
 * `out` before `low`, always: a row at zero is not "nearly empty", and a screen that draws it as a
 * warning invites somebody to promise units that do not exist. A row with no threshold configured
 * can still be `out` — nobody has to opt in to being told the shelf is empty.
 */
export const STOCK_STATUSES = ['in_stock', 'low', 'out'] as const;
export type StockStatus = (typeof STOCK_STATUSES)[number];

export const STOCK_STATUS_LABELS: Record<StockStatus, string> = {
  in_stock: 'תקין',
  low: 'מלאי נמוך',
  out: 'אזל מהמלאי'
};

export const STOCK_STATUS_OPTIONS: PhOption<StockStatus>[] = phOptions(STOCK_STATUSES, STOCK_STATUS_LABELS);

/** `available` and the threshold in force → the pill. One owner, so the list and the card agree. */
export const resolveStockStatus = (available: number, threshold: number | null): StockStatus => {
  if (available <= 0) return 'out';
  return threshold !== null && available <= threshold ? 'low' : 'in_stock';
};

/* ------------------------------------------------------------------ */
/* Ledger                                                             */
/* ------------------------------------------------------------------ */

/**
 * Why stock moved (spec §31-32).
 *
 * `sale` and `return` exist in the vocabulary but nothing writes them in the MVP — there is no
 * Orders domain yet, which is a declared gap rather than an oversight. They are listed so the
 * column's value set does not have to change when Orders arrives.
 */
export const TRANSACTION_TYPES = [
  'receipt',
  'adjustment',
  'transfer_in',
  'transfer_out',
  'sale',
  'return',
  'damage'
] as const;
export type TransactionType = (typeof TRANSACTION_TYPES)[number];

export const TRANSACTION_TYPE_LABELS: Record<TransactionType, string> = {
  receipt: 'קליטת מלאי',
  adjustment: 'התאמת מלאי',
  transfer_in: 'העברה — כניסה',
  transfer_out: 'העברה — יציאה',
  sale: 'מכירה',
  return: 'החזרה',
  damage: 'נזק'
};

export const TRANSACTION_TYPE_OPTIONS = phOptions(TRANSACTION_TYPES, TRANSACTION_TYPE_LABELS);

/** The types a human may write directly. Transfers and sales are produced by their own commands. */
export const MANUAL_TRANSACTION_TYPES: readonly TransactionType[] = ['receipt', 'adjustment', 'damage'];
